/**
 * Polls device logs (logcat/syslog/crashlog) from Appium driver.
 * Writes collected entries to JSONL via JsonlFileWriter.
 */

import type { DeviceLogEntry, DeviceLogSource, Platform } from './types.js';
import { JsonlFileWriter } from './jsonl-stream.js';

export interface DeviceLogCollectorOptions {
  readonly pollInterval: number;
  readonly platform: Platform;
  readonly enabled: boolean;
}

type LogEntry = { level: string; timestamp: number; message: string };
type DriverLike = {
  getLogs(type: string): Promise<LogEntry[]>;
  getLogTypes(): Promise<string[]>;
};

const PLATFORM_LOG_TYPES: Record<Platform, string[]> = {
  android: ['logcat'],
  ios: ['syslog', 'crashlog'],
  unknown: [],
};

function normalizeLevel(raw: string): DeviceLogEntry['level'] {
  const lower = raw.toLowerCase();
  if (lower === 'verbose' || lower === 'v' || lower === 'all') return 'verbose';
  if (lower === 'debug' || lower === 'd') return 'debug';
  if (lower === 'info' || lower === 'i') return 'info';
  if (lower === 'warn' || lower === 'warning' || lower === 'w') return 'warn';
  if (lower === 'error' || lower === 'err' || lower === 'e' || lower === 'severe') return 'error';
  if (lower === 'fatal' || lower === 'f') return 'fatal';
  return 'info';
}

export class DeviceLogCollector {
  private readonly writer: JsonlFileWriter;
  private readonly options: DeviceLogCollectorOptions;
  private intervalHandle: ReturnType<typeof setInterval> | null = null;
  private driver: DriverLike | null = null;
  private availableLogTypes: string[] = [];
  private disposed = false;
  private entries: DeviceLogEntry[] = [];

  constructor(options: DeviceLogCollectorOptions) {
    this.options = options;
    this.writer = new JsonlFileWriter('device-logs');
  }

  async start(driver: DriverLike): Promise<void> {
    if (!this.options.enabled || this.disposed) return;
    this.driver = driver;

    try {
      this.availableLogTypes = await driver.getLogTypes();
    } catch {
      this.availableLogTypes = [];
    }

    this.intervalHandle = setInterval(() => {
      void this.poll();
    }, this.options.pollInterval);
  }

  async poll(): Promise<void> {
    if (!this.driver || this.disposed) return;

    const targetTypes = PLATFORM_LOG_TYPES[this.options.platform] ?? [];
    for (const logType of targetTypes) {
      if (!this.availableLogTypes.includes(logType)) continue;
      try {
        const entries = await this.driver.getLogs(logType);
        for (const entry of entries) {
          const normalized: DeviceLogEntry = {
            timestamp: new Date(entry.timestamp).toISOString(),
            level: normalizeLevel(entry.level),
            message: entry.message,
            source: logType as DeviceLogSource,
          };
          this.entries.push(normalized);
          this.writer.append(normalized);
        }
      } catch {
        // Driver may disconnect mid-poll; silently skip
      }
    }
  }

  stop(): void {
    if (this.intervalHandle !== null) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
  }

  getEntries(): DeviceLogEntry[] {
    return [...this.entries];
  }

  /** Returns only entries appended after the given offset index (for per-test slicing). */
  getEntriesSince(offset: number): DeviceLogEntry[] {
    return this.entries.slice(offset);
  }

  getPath(): string {
    return this.writer.getPath();
  }

  getCount(): number {
    return this.writer.getCount();
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    this.writer.close();
  }

  cleanup(): void {
    this.dispose();
    this.writer.cleanup();
  }
}
